'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import {
  createEstampita,
  deleteEstampita,
  exportarEstampita,
  getEstampitas,
  getEstampitasManifestState,
  pintarEstampita,
  regenerarManifiestoEstampitas,
  updateEstampita,
  uploadEstampitaImage,
  ESTAMPITA_ANCHO,
  ESTAMPITA_ALTO,
  type EncuadreEstampita,
} from '@/lib/actions';
import type { EstampitaDoc } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookImage,
  Clipboard,
  Eye,
  EyeOff,
  Loader2,
  Maximize2,
  Minimize2,
  MoveLeft,
  MoveRight,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  Trash2,
  Upload,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/** Tamaño de la vista previa del editor (misma proporción que el marco real). */
const PREV_W = 260;
const PREV_H = Math.round((PREV_W * ESTAMPITA_ALTO) / ESTAMPITA_ANCHO);

const FONDOS: { id: string; color: string; nombre: string }[] = [
  { id: 'blanco', color: '#ffffff', nombre: 'Blanco' },
  { id: 'crema', color: '#f2ead5', nombre: 'Crema' },
  { id: 'negro', color: '#111111', nombre: 'Negro' },
  { id: 'oro', color: '#c8a44b', nombre: 'Oro' },
];

// ─── Editor de encuadre ──────────────────────────────────────────────────────
// El marco es SIEMPRE 2:3 (la forma de una estampita). La imagen se mueve y se
// escala dentro; lo que sobra se recorta y lo que falta se rellena con el fondo.
// Así una foto apaisada no produce una estampita apaisada: la encuadra una
// persona, no un `object-fit`.

function EstampitaEditor({
  estampita,
  siguienteOrden,
  onClose,
  onSaved,
}: {
  estampita: EstampitaDoc | null;
  siguienteOrden: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [nombre, setNombre] = useState(estampita?.nombre || '');
  const [bitmap, setBitmap] = useState<ImageBitmap | null>(null);
  const [dims, setDims] = useState({ iw: 0, ih: 0 });
  const [enc, setEnc] = useState<EncuadreEstampita>({ escala: 1, cx: ESTAMPITA_ANCHO / 2, cy: ESTAMPITA_ALTO / 2, fondo: '#ffffff' });
  const [guardando, setGuardando] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const arrastre = useRef<{ x: number; y: number; cx: number; cy: number } | null>(null);

  const escalaCover = useMemo(
    () => (dims.iw ? Math.max(ESTAMPITA_ANCHO / dims.iw, ESTAMPITA_ALTO / dims.ih) : 1),
    [dims],
  );
  const escalaContain = useMemo(
    () => (dims.iw ? Math.min(ESTAMPITA_ANCHO / dims.iw, ESTAMPITA_ALTO / dims.ih) : 1),
    [dims],
  );

  const cargarImagen = useCallback(
    async (files: (File | Blob)[]) => {
      const valido = files.find((f) => f.type?.startsWith('image/'));
      if (!valido) return;
      try {
        const bm = await createImageBitmap(valido);
        setBitmap(bm);
        setDims({ iw: bm.width, ih: bm.height });
        // Se abre RELLENANDO el marco: es lo que casi siempre se quiere, y desde
        // ahí se aleja quien prefiera verla entera.
        const cover = Math.max(ESTAMPITA_ANCHO / bm.width, ESTAMPITA_ALTO / bm.height);
        setEnc((p) => ({ ...p, escala: cover, cx: ESTAMPITA_ANCHO / 2, cy: ESTAMPITA_ALTO / 2 }));
      } catch (e: any) {
        toast({ title: 'Error', description: `No se pudo leer la imagen: ${e.message}`, variant: 'destructive' });
      }
    },
    [toast],
  );

  // Pegar con Ctrl+V mientras el formulario está abierto (patrón de la galería).
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      const ficheros = Array.from(e.clipboardData?.items || [])
        .filter((i) => i.kind === 'file' && i.type.startsWith('image/'))
        .map((i) => i.getAsFile())
        .filter(Boolean) as File[];
      if (!ficheros.length) return;
      e.preventDefault();
      cargarImagen(ficheros);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [cargarImagen]);

  // Vista previa: la pinta la MISMA función que exporta el PNG, así que lo que
  // se ve aquí es exactamente lo que acaba en el juego.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    if (!bitmap) {
      ctx.clearRect(0, 0, PREV_W, PREV_H);
      return;
    }
    pintarEstampita(ctx, bitmap, dims.iw, dims.ih, enc, PREV_W);
  }, [bitmap, dims, enc]);

  // La rueda se escucha A MANO y no con onWheel: React registra wheel como
  // pasivo, así que preventDefault() no haría nada y el diálogo scrollearía.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c || !bitmap) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      setEnc((p) => ({ ...p, escala: Math.max(escalaContain * 0.25, Math.min(escalaCover * 6, p.escala * (e.deltaY < 0 ? 1.1 : 1 / 1.1))) }));
    };
    c.addEventListener('wheel', onWheel, { passive: false });
    return () => c.removeEventListener('wheel', onWheel);
  }, [bitmap, escalaCover, escalaContain]);

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!bitmap) return;
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
    arrastre.current = { x: e.clientX, y: e.clientY, cx: enc.cx, cy: enc.cy };
  };
  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const a = arrastre.current;
    if (!a) return;
    // El ratón se mueve en píxeles de la VISTA PREVIA; el encuadre vive en
    // píxeles del marco final, de ahí la conversión.
    const k = ESTAMPITA_ANCHO / PREV_W;
    setEnc((p) => ({ ...p, cx: a.cx + (e.clientX - a.x) * k, cy: a.cy + (e.clientY - a.y) * k }));
  };
  const onPointerUp = () => {
    arrastre.current = null;
  };

  const encuadrar = (modo: 'cover' | 'contain') => {
    setEnc((p) => ({
      ...p,
      escala: modo === 'cover' ? escalaCover : escalaContain,
      cx: ESTAMPITA_ANCHO / 2,
      cy: ESTAMPITA_ALTO / 2,
    }));
  };

  const guardar = async () => {
    if (!nombre.trim()) {
      toast({ title: 'Falta el nombre', description: 'Es lo que dice el nazareno al dártela.', variant: 'destructive' });
      return;
    }
    if (!bitmap && !estampita) {
      toast({ title: 'Falta la imagen', description: 'Pega o sube la foto de la estampita.', variant: 'destructive' });
      return;
    }
    setGuardando(true);
    try {
      let campos: Partial<EstampitaDoc> = { nombre: nombre.trim() };
      if (bitmap) {
        const blob = await exportarEstampita(bitmap, dims.iw, dims.ih, enc);
        const sub = await uploadEstampitaImage(blob);
        if (!sub.success || !sub.url || !sub.hash) throw new Error(sub.message || 'No se pudo subir.');
        campos = { ...campos, imageUrl: sub.url, hash: sub.hash, w: ESTAMPITA_ANCHO, h: ESTAMPITA_ALTO };
      }
      const res = estampita
        ? await updateEstampita(estampita.id, campos)
        : await createEstampita({
            nombre: campos.nombre!,
            imageUrl: campos.imageUrl!,
            hash: campos.hash!,
            w: ESTAMPITA_ANCHO,
            h: ESTAMPITA_ALTO,
            order: siguienteOrden,
          });
      if (!res.success) throw new Error(res.message);
      toast({ title: 'Guardado', description: `${res.message} Aparecerá en el juego en unos minutos.` });
      onSaved();
      onClose();
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setGuardando(false);
    }
  };

  const zoomPct = dims.iw ? Math.round((enc.escala / escalaCover) * 100) : 100;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[90dvh] max-w-3xl flex-col p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="flex items-center gap-2">
            <BookImage className="h-5 w-5 text-primary" />
            {estampita ? 'Editar estampita' : 'Nueva estampita'}
          </DialogTitle>
          <DialogDescription>
            La estampita mide siempre {ESTAMPITA_ANCHO}×{ESTAMPITA_ALTO} (2:3). Coloca la imagen dentro del marco:
            arrastra para moverla y usa la rueda o el deslizador para acercarla.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
          <div className="grid gap-6 sm:grid-cols-[260px_1fr]">
            {/* Marco + controles de encuadre */}
            <div>
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  cargarImagen(Array.from(e.dataTransfer.files));
                }}
                className={cn(
                  'relative overflow-hidden rounded-2xl border-2',
                  dragOver ? 'border-primary' : 'border-white/15',
                )}
                style={{ width: PREV_W, height: PREV_H }}
              >
                {bitmap ? (
                  <canvas
                    ref={canvasRef}
                    width={PREV_W}
                    height={PREV_H}
                    className="cursor-move touch-none"
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                  />
                ) : estampita ? (
                  // Sin imagen nueva se enseña la que ya tiene. NO se puede
                  // recuadrar: Firebase Storage no manda cabeceras CORS, así que
                  // el navegador no deja leer en un canvas una imagen ya subida.
                  <img src={estampita.imageUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="flex h-full w-full flex-col items-center justify-center gap-2 bg-white/[0.02] text-center hover:bg-white/[0.05]"
                  >
                    <div className="flex items-center gap-3 text-primary">
                      <Clipboard className="h-5 w-5" />
                      <Upload className="h-5 w-5" />
                    </div>
                    <p className="text-xs font-semibold">Pega, arrastra o haz clic</p>
                  </button>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    cargarImagen(Array.from(e.target.files || []));
                    e.target.value = '';
                  }}
                />
              </div>

              {bitmap && (
                <div className="mt-3 space-y-3" style={{ width: PREV_W }}>
                  <input
                    type="range"
                    min={escalaContain * 0.25}
                    max={escalaCover * 6}
                    step={escalaCover / 200}
                    value={enc.escala}
                    onChange={(e) => setEnc({ ...enc, escala: Number(e.target.value) })}
                    className="w-full accent-primary"
                  />
                  <div className="flex flex-wrap gap-1">
                    <Button size="sm" variant="secondary" className="flex-1" onClick={() => encuadrar('cover')}>
                      <Maximize2 className="h-3.5 w-3.5" />
                      Rellenar
                    </Button>
                    <Button size="sm" variant="secondary" className="flex-1" onClick={() => encuadrar('contain')}>
                      <Minimize2 className="h-3.5 w-3.5" />
                      Entera
                    </Button>
                    <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => fileRef.current?.click()} title="Cambiar imagen">
                      <RotateCcw className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[0.7rem] text-muted-foreground">Fondo</span>
                    {FONDOS.map((f) => (
                      <button
                        key={f.id}
                        type="button"
                        title={f.nombre}
                        onClick={() => setEnc({ ...enc, fondo: f.color })}
                        className={cn(
                          'h-5 w-5 rounded-full border-2 transition-transform',
                          enc.fondo === f.color ? 'border-primary scale-110' : 'border-white/25',
                        )}
                        style={{ background: f.color }}
                      />
                    ))}
                    <span className="ml-auto text-[0.7rem] text-muted-foreground">{zoomPct}%</span>
                  </div>
                  <p className="text-[0.7rem] text-muted-foreground">
                    Original {dims.iw}×{dims.ih} px
                  </p>
                </div>
              )}

              {!bitmap && estampita && (
                <p className="mt-2 text-[0.7rem] text-muted-foreground" style={{ width: PREV_W }}>
                  Para recolocar la imagen, vuelve a elegirla:{' '}
                  <button type="button" className="text-primary underline" onClick={() => fileRef.current?.click()}>
                    cambiar imagen
                  </button>
                  .
                </p>
              )}
            </div>

            {/* Datos */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre de la estampita</Label>
                <Input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="La Macarena" autoFocus />
                <p className="text-xs text-muted-foreground">
                  En el juego se lee «Estampita de <span className="text-primary">{nombre || '…'}</span>», y es lo que
                  dice el nazareno al dártela: «Toma una estampita de {nombre || '…'}».
                </p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-xs text-muted-foreground">
                <p className="font-semibold text-foreground">Cómo llega al juego</p>
                <p className="mt-1.5">
                  Al guardar se republica el catálogo. Cada jugador lo comprueba cada 5 minutos y solo se descarga las
                  estampitas que le falten: la imagen se guarda con el hash de su contenido, así que una que no cambia no
                  se vuelve a bajar nunca.
                </p>
                <p className="mt-2">
                  Dentro del juego se ve en la mano y en el inventario, y con <strong>clic derecho</strong> se abre a
                  pantalla completa.
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="border-t border-white/10 px-6 py-4">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={guardar} disabled={guardando}>
            {guardando && <Loader2 className="h-4 w-4 animate-spin" />}
            {estampita ? 'Guardar cambios' : 'Crear estampita'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Página ──────────────────────────────────────────────────────────────────

export default function AdminEstampitasPage() {
  const { userData, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [items, setItems] = useState<EstampitaDoc[]>([]);
  const [cargando, setCargando] = useState(true);
  const [editando, setEditando] = useState<EstampitaDoc | null>(null);
  const [creando, setCreando] = useState(false);
  const [borrando, setBorrando] = useState<EstampitaDoc | null>(null);
  const [regenerando, setRegenerando] = useState(false);
  const [manifiesto, setManifiesto] = useState<{ count: number; hash: string; generatedAt: number } | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    const [lista, estado] = await Promise.all([getEstampitas(), getEstampitasManifestState()]);
    setItems(lista);
    setManifiesto(estado);
    setCargando(false);
  }, []);

  useEffect(() => {
    if (loading) return;
    if (userData?.role !== 'admin') {
      navigate('/jartinet/home');
      return;
    }
    cargar();
  }, [loading, userData, navigate, cargar]);

  const activas = useMemo(() => items.filter((e) => e.activa !== false).length, [items]);
  const siguienteOrden = useMemo(() => (items.length ? Math.max(...items.map((e) => e.order ?? 0)) + 1 : 1), [items]);

  const alternarActiva = async (e: EstampitaDoc) => {
    const res = await updateEstampita(e.id, { activa: e.activa === false });
    if (!res.success) toast({ title: 'Error', description: res.message, variant: 'destructive' });
    cargar();
  };

  /** Intercambia el orden con el vecino. Es el orden del creativo del mod. */
  const mover = async (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const a = items[i];
    const b = items[j];
    const copia = [...items];
    [copia[i], copia[j]] = [copia[j], copia[i]];
    setItems(copia);
    await Promise.all([updateEstampita(a.id, { order: b.order ?? j }), updateEstampita(b.id, { order: a.order ?? i })]);
    cargar();
  };

  const eliminar = async () => {
    if (!borrando) return;
    const res = await deleteEstampita(borrando.id);
    toast({
      title: res.success ? 'Eliminada' : 'Error',
      description: res.success ? 'Desaparecerá del juego en el próximo repaso.' : res.message,
      variant: res.success ? undefined : 'destructive',
    });
    setBorrando(null);
    cargar();
  };

  const regenerar = async () => {
    setRegenerando(true);
    const res = await regenerarManifiestoEstampitas();
    setRegenerando(false);
    toast({
      title: res.success ? 'Listo' : 'Error',
      description: res.message,
      variant: res.success ? undefined : 'destructive',
    });
    cargar();
  };

  if (loading || cargando) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  const desincronizado = manifiesto !== null && manifiesto.count !== activas;

  return (
    <div className="space-y-8 pb-10">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-primary/15 via-card/60 to-transparent p-7">
        <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-primary/20 blur-3xl" />
        <div className="relative flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 place-items-center rounded-2xl border border-primary/30 bg-primary/10">
                <BookImage className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="font-headline text-3xl font-bold md:text-4xl">Estampitas</h1>
                <p className="text-sm text-muted-foreground">
                  Las que reparten los penitentes dentro del juego. La imagen no va en el mod: se descarga de aquí.
                </p>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold">
                {activas} activa{activas === 1 ? '' : 's'}
              </span>
              {items.length !== activas && (
                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-muted-foreground">
                  {items.length - activas} oculta{items.length - activas === 1 ? '' : 's'}
                </span>
              )}
              {manifiesto && (
                <span
                  className={cn(
                    'rounded-full border px-3 py-1 text-xs font-semibold',
                    desincronizado
                      ? 'border-amber-500/40 bg-amber-500/10 text-amber-300'
                      : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
                  )}
                >
                  {desincronizado
                    ? `Publicadas ${manifiesto.count} — hay cambios sin publicar`
                    : `Publicadas ${manifiesto.count} en el juego`}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={regenerar} disabled={regenerando}>
              {regenerando ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Republicar
            </Button>
            <Button size="lg" onClick={() => setCreando(true)}>
              <Plus className="h-5 w-5" />
              Nueva estampita
            </Button>
          </div>
        </div>
      </div>

      {items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-white/15 py-16 text-center">
          <BookImage className="mx-auto h-10 w-10 text-muted-foreground/50" />
          <p className="mt-3 font-semibold">Todavía no hay estampitas</p>
          <p className="mx-auto mt-1 max-w-lg text-sm text-muted-foreground">
            Crea la primera con su nombre y su foto. Aparecerá sola en la pestaña de creativo del mod, sin tener que
            actualizar nada ni volver a compilar.
          </p>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <AnimatePresence>
          {items.map((e, i) => (
            <motion.div
              key={e.id}
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className={cn(
                'group relative overflow-hidden rounded-2xl border border-white/10 bg-card/40',
                e.activa === false && 'opacity-50',
              )}
            >
              <div className="relative aspect-[2/3] overflow-hidden bg-black/40">
                <img
                  src={e.imageUrl}
                  alt=""
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {e.activa === false && (
                  <span className="absolute left-2 top-2 rounded-full bg-black/80 px-2 py-0.5 text-[0.6rem] font-bold uppercase text-white">
                    Oculta
                  </span>
                )}
              </div>

              <div className="p-3">
                <h3 className="truncate text-sm font-bold" title={e.nombre}>
                  {e.nombre}
                </h3>
                <p className="mt-0.5 truncate text-[0.7rem] text-muted-foreground">
                  {e.w || '?'}×{e.h || '?'} · {e.hash?.slice(0, 8)}
                </p>

                <div className="mt-3 flex flex-wrap gap-1">
                  <Button size="sm" variant="secondary" className="flex-1" onClick={() => setEditando(e)}>
                    <Pencil className="h-3.5 w-3.5" />
                    Editar
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => alternarActiva(e)}
                    title={e.activa === false ? 'Mostrar' : 'Ocultar'}
                  >
                    {e.activa === false ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button size="icon" variant="outline" className="text-destructive" onClick={() => setBorrando(e)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="mt-1 flex gap-1">
                  <Button size="sm" variant="ghost" className="h-7 flex-1" disabled={i === 0} onClick={() => mover(i, -1)}>
                    <MoveLeft className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 flex-1"
                    disabled={i === items.length - 1}
                    onClick={() => mover(i, 1)}
                  >
                    <MoveRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {(creando || editando) && (
        <EstampitaEditor
          estampita={editando}
          siguienteOrden={siguienteOrden}
          onClose={() => {
            setCreando(false);
            setEditando(null);
          }}
          onSaved={cargar}
        />
      )}

      <AlertDialog open={!!borrando} onOpenChange={(o) => !o && setBorrando(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar «{borrando?.nombre}»?</AlertDialogTitle>
            <AlertDialogDescription>
              Dejará de repartirse en el juego y desaparecerá del creativo. Las que ya tenga alguien en el inventario se
              quedarán sin imagen. Si solo quieres retirarla un tiempo, es mejor ocultarla.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={eliminar}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
